import { loginCreverseParent, loginCreverseStudent } from '@mathflat/creverse-auth/two-phase'
import {
  authApi,
  type CreverseParentLoginRequest,
  type CreverseStudentLoginRequest,
  type LoginRequest,
} from '@mathflat/domain/Auth'
import type { Student } from '@mathflat/domain/Student'
import { jwtTokenService } from '@mathflat/shared/@modules/auth/jwtTokenService'
import { loginWithVine, type VineFetcher } from '@mathflat/vine-auth/login'
import type { VineLoginRequest, VineLoginResponse } from '@mathflat/vine-auth/types'

import { loadInitialData } from '~/@app/init/loadInitialData'

import { creverseFetcher } from '../api/creverseFetcher'
import { LATEST_DEVICE_VERSION, type LoginEntryPath, publicRoutes, routeName } from '../constants'
import { crossOriginLogoutClear } from '../integration/crossOriginLogoutClear'
import { probeReceiverDeployed } from '../integration/handleStudentUpLogic'
import { getDomainPair, isSenderSide } from '../integration/integrationDomain'
import { envUtils } from '../utils/envUtils'
import { reporter } from '../utils/errorReporting/reporter'
import { writeLoginRole } from '../utils/loginRoleCookie'
import maxios from '../utils/maxios'
import { webviewService } from '.'
import { seedParentSession } from './parentSession.service'
import { commonRepo } from './repo.service'
import { localStorageService } from './storage.service'

// 무효 토큰이 401 로 내려온다. withoutAuth 로 보내야 JWT 재발급 인터셉터가 세션 만료로 오해하지 않는다.
const vineFetcher: VineFetcher<VineLoginRequest, VineLoginResponse> = (path, body) =>
  maxios.post<VineLoginResponse>(path, body, { withoutAuth: true })

const DISCARD_LOGOUT_WAIT_MS = 3000

export class AuthService {
  get isKeepSignInChecked() {
    return jwtTokenService.isUsingLocalStorage
  }

  /**
   * jwtTokenService 의 환경/플랫폼/이벤트 핸들러 셋업만 수행
   * 통합 마이그레이션 흐름 (handleStudentMainLogic 등) 이 jwtTokenService 의 baseURL 등을
   * 필요로 하기 때문에, initialize.ts 에서 통합 분기 전에 먼저 호출.
   */
  prepareJwtAuth() {
    const environment = envUtils.isLive
      ? 'live'
      : envUtils.isStaging
        ? 'stg'
        : localStorage.getItem('DEV_API') ?? 'dev'

    const isNamedEnv = environment === 'live' || environment === 'stg' || environment === 'dev'
    jwtTokenService.init({
      env: isNamedEnv ? environment : 'custom',
      customURL: isNamedEnv ? undefined : environment,
      platform: 'STUDENT',
    })

    const redirectToLoginPath = () => {
      if (publicRoutes.has(window.location.pathname)) return
      window.location.replace(
        (localStorageService.loginPath ?? routeName.login) + window.location.search,
      )
    }

    jwtTokenService.setEventHandlers({
      onTokenCleared: redirectToLoginPath,
      onTokenNotFound: redirectToLoginPath,
      onTokenReceived: () => {
        // ★ hash 마이그레이션 진행 중에는 reload 하지 않음 — 무한 reload 방지
        //   migrateFromHashParam 이 setTokens('LOGIN') broadcast 일으키면 same-origin frame 들이 onTokenReceived 발화 → reload → URL ?hash 그대로 → 다시 init → 다시 마이그 → 다시 broadcast → 무한
        //   hash cleanup 이 끝난 후의 reload (다른 탭 로그인 등) 만 정상 작동
        if (new URLSearchParams(window.location.search).has('hash')) {
          return
        }
        window.location.reload()
      },
      onReissueSuccess: ({ restrictions }) => {
        commonRepo.permission.updateRestrictions(restrictions ?? [])
      },
    })
  }

  async initializeJwtAuth(options?: { skipInitializeAuth?: boolean }) {
    if (options?.skipInitializeAuth) return

    await jwtTokenService.initializeAuth()
  }

  get userType() {
    if (localStorageService.user.parentId) {
      return 'PARENT'
    }
    return 'STUDENT'
  }

  get isLoggedIn() {
    return jwtTokenService.isLoggedIn
  }

  /**
   * JWT 로그인 응답 처리와 LOGIN_PATH 기록은 항상 쌍으로 —
   * 스탬프를 빠뜨린 로그인 메서드가 생기면 이전 SSO 세션의 LOGIN_PATH 가 남아
   * 세션 만료 시 엉뚱한 로그인 페이지로 튕긴다.
   */
  private async finalizeJwtLogin(
    response: Parameters<typeof jwtTokenService.processJwtLoginResponse>[0],
    loginPath: LoginEntryPath = routeName.login,
  ) {
    await jwtTokenService.processJwtLoginResponse(response)
    localStorageService.setLoginPath(loginPath)
  }

  async loginFromBasicStudentApp({
    accessToken,
    refreshToken,
    isKeepSignInChecked,
  }: {
    accessToken: string
    refreshToken: string
    isKeepSignInChecked: boolean
  }) {
    if (isKeepSignInChecked) {
      jwtTokenService.useLocalStorage()
    }

    jwtTokenService.setTokens({ accessToken, refreshToken })
    await this.finalizeJwtLogin({ accessToken, refreshToken })
  }

  async loginByJwt(loginRequest: LoginRequest, keepLoggedIn: boolean, beforeLoginId: string) {
    if (keepLoggedIn) {
      jwtTokenService.useLocalStorage()
    }

    // 로그인 거부도 401 UNAUTHORIZED 로 온다. 화면이 아이디 칸에 안내하므로 공통 토스트는 이 코드만 막는다
    const response = await authApi.login(loginRequest, { preventErrorToast: ['UNAUTHORIZED'] })

    // response가 undefined이거나 accessToken이 없는 경우 Error 객체로 throw
    // (raw response를 throw하면 undefined인 경우 catch에서 instanceof 체크가 모두 실패하여 silent하게 무시됨)
    if (!response?.accessToken) throw new Error('로그인 응답이 유효하지 않습니다.')

    await this.finalizeJwtLogin(response)

    localStorageService.update({
      user: {
        beforeLoginId,
        ...(response.userType === 'STUDENT'
          ? { studentId: response.userId as Student['id'] }
          : { parentId: response.userId }),
        academyId: response.academyId,
        isPasswordChangedRequired: response.passwordChangedRequired,
      },
      device: { ver: LATEST_DEVICE_VERSION },
    })

    if (response.userType === 'STUDENT') {
      writeLoginRole('student')
      await loadInitialData(response.userId as Student['id'], response.restrictions)
    }

    return response
  }

  /**
   * 네이버 웍스(웨일스페이스) SSO 로그인.
   * - OAuth 2.0 콜백에서 받은 code로 서버에 JWT 로그인 요청을 보낸다.
   * - SSO 로그인 특성상 "로그인 유지"를 기본으로 한다(keepLoggedIn=true 사용).
   */
  async loginByNaverWorks(code: string, keepLoggedIn: boolean) {
    if (keepLoggedIn) {
      jwtTokenService.useLocalStorage()
    }

    const response = await authApi.loginWithNaverWorks({
      code,
      userType: 'STUDENT',
      serviceType: 'MATHFLAT',
    })

    if (!response?.accessToken) {
      throw response
    }

    await this.finalizeJwtLogin(response)

    localStorageService.update({
      user: {
        ...(response.userType === 'STUDENT'
          ? { studentId: response.userId as Student['id'] }
          : { parentId: response.userId }),
        academyId: response.academyId,
        isPasswordChangedRequired: response.passwordChangedRequired,
      },
      device: { ver: LATEST_DEVICE_VERSION },
    })

    if (response.userType === 'STUDENT') {
      writeLoginRole('student')
      await loadInitialData(response.userId as Student['id'], response.restrictions)
    }

    return response
  }

  /**
   * AIEP(광주교육청 통합 로그인) SAML 브릿지 로그인.
   * - /v2/login/saml2/aiep 는 SAML 콜백 code 를 받아 JWT 세트를 바로 발급한다.
   * - AIEP 는 학생 전용 SSO 이므로 응답이 STUDENT 가 아니면 에러로 던진다.
   */
  async loginByAiep(code: string, keepLoggedIn: boolean) {
    if (keepLoggedIn) {
      jwtTokenService.useLocalStorage()
    }

    const response = await authApi.loginWithAiep({ code, userType: 'STUDENT' })

    if (!response?.accessToken) throw new Error('AIEP 로그인 응답이 유효하지 않습니다.')

    if (response.userType !== 'STUDENT') {
      throw new Error('STUDENT_ONLY')
    }

    await this.finalizeJwtLogin(response)

    const studentId = response.userId as Student['id']
    localStorageService.update({
      user: {
        studentId,
        academyId: response.academyId,
        isPasswordChangedRequired: response.passwordChangedRequired,
      },
      device: { ver: LATEST_DEVICE_VERSION },
    })

    writeLoginRole('student')
    await loadInitialData(studentId, response.restrictions)

    return response
  }

  /**
   * 크레버스 SSO 2페이즈 로그인.
   * - 1페이즈(users/by-token)에서 받은 서명 + 선택 지점(brchId)으로 JWT 세트를 발급받는다.
   */
  async loginByCreverse(loginRequest: CreverseStudentLoginRequest) {
    // SSO 로그인 특성상 "로그인 유지"를 기본으로 한다
    jwtTokenService.useLocalStorage()

    const response = await loginCreverseStudent(creverseFetcher, loginRequest)

    if (!response?.accessToken || response.userType !== 'STUDENT') {
      return null
    }

    await this.finalizeJwtLogin(response, routeName.loginCreverse)

    // 앞서 학부모로 로그인했던 기기면 parentId 가 남아 userType 이 PARENT 로 판정된다
    localStorageService.clearParentSession()

    const studentId = response.userId as Student['id']
    localStorageService.update({
      user: {
        studentId,
        academyId: response.academyId,
        isPasswordChangedRequired: response.passwordChangedRequired,
      },
      device: { ver: LATEST_DEVICE_VERSION },
    })

    writeLoginRole('student')
    await loadInitialData(studentId, response.restrictions)

    return response
  }

  /**
   * 바인그룹 SSO 로그인. 일회용 token 하나로 JWT 세트를 바로 발급받는다 — 센터 선택 단계가 없다.
   */
  async loginByVine(token: string) {
    // SSO 로그인 특성상 "로그인 유지"를 기본으로 한다
    jwtTokenService.useLocalStorage()

    const response = await loginWithVine(vineFetcher, token)

    if (!response?.accessToken || response.userType !== 'STUDENT') {
      return null
    }

    await this.finalizeJwtLogin(response, routeName.loginVine)

    const studentId = response.userId as Student['id']
    localStorageService.update({
      user: {
        studentId,
        academyId: response.academyId,
        isPasswordChangedRequired: response.passwordChangedRequired,
      },
      device: { ver: LATEST_DEVICE_VERSION },
    })

    writeLoginRole('student')
    await loadInitialData(studentId, response.restrictions)

    return response
  }

  /**
   * 토큰 발급 뒤에 실패한 로그인을 되돌린다.
   * authApi.logout() 과 달리 페이지를 옮기지 않으므로 호출부가 실패를 안내할 수 있다.
   */
  private async discardFailedLogin(issued: { refreshToken: string }) {
    // JWT 헤더는 비동기 요청 인터셉터가 붙이므로 토큰을 지우기 전에 요청이 나가야 한다.
    // 다만 maxios 에 timeout 이 없어 응답을 무한정 기다리면 실패 안내가 막히므로 상한을 둔다.
    await Promise.race([
      authApi
        .logout({ refreshToken: issued.refreshToken, deviceId: localStorageService.deviceId })
        .catch(() => undefined),
      new Promise((resolve) => setTimeout(resolve, DISCARD_LOGOUT_WAIT_MS)),
    ])

    localStorageService.clear()
    jwtTokenService.clearTokens()
  }

  /**
   * 크레버스 SSO 학부모 2페이즈 로그인.
   * 자녀 시딩까지 성공해야 로그인으로 본다 — 활성 자녀 없이 진입하면 빈 화면이 된다.
   */
  async loginByCreverseParent(loginRequest: CreverseParentLoginRequest) {
    jwtTokenService.useLocalStorage()

    const response = await loginCreverseParent(creverseFetcher, loginRequest)

    if (!response?.accessToken || response.userType !== 'PARENT') {
      return null
    }

    await this.finalizeJwtLogin(response, routeName.loginCreverse)

    localStorageService.update({
      user: {
        parentId: response.userId,
        academyId: response.academyId,
        isPasswordChangedRequired: response.passwordChangedRequired,
      },
      device: { ver: LATEST_DEVICE_VERSION },
    })

    if (!(await seedParentSession(response.userId, response.restrictions))) {
      await this.discardFailedLogin(response)
      return null
    }

    return response
  }

  /**
   * 선생님앱 iframe에서 학생 대리 로그인.
   */
  async loginByStudentImpersonationAuthToken(token: string) {
    const response = await authApi.loginWithStudentImpersonationAuthToken(token)
    await this.finalizeJwtLogin(response)

    // 학부모 → 학생 대리 로그인이므로 이전 학부모 세션을 정리한다.
    // update()는 lodash merge 라 parentId 를 안 비우면 userType 이 PARENT 로 남는다.
    localStorageService.clearParentSession()

    localStorageService.update({
      user: {
        studentId: response.userId as Student['id'],
        academyId: response.academyId,
      },
      device: { ver: LATEST_DEVICE_VERSION },
    })

    return response
  }

  async logout() {
    // 아래 localStorageService.clear() 가 LOGIN_PATH 까지 지우므로 리다이렉트 목적지를 먼저 캡처한다
    const loginPath = localStorageService.loginPath ?? routeName.login
    const legacyToken = jwtTokenService.getLegacyToken()
    const refreshToken = jwtTokenService.getRefreshToken()

    const logoutPromises: Promise<unknown>[] = []
    if (legacyToken) {
      logoutPromises.push(authApi.logoutLegacy(legacyToken))
    }
    if (refreshToken) {
      const deviceId = localStorageService.deviceId
      logoutPromises.push(authApi.logout({ refreshToken, deviceId }))
    }

    // API 요청이 완료될 때까지 대기 (실패해도 계속 진행)
    await Promise.allSettled(logoutPromises)

    // ★ sender (student-up) 에서만 probe 결과 의미 있음 — receiver 는 자기 /login 으로 가니 무관.
    //   Phase 1 (반대편 student 가 아직 베이직) 케이스에서 probe 실패 시 베이직 #/logout 으로 fallback.
    const isOppositeIntegrated = isSenderSide() ? await probeReceiverDeployed() : true

    // 반대 origin (student ↔ student-up) 의 storage 도 정리. 반대편이 통합인 경우만 의미 있음.
    if (isOppositeIntegrated) {
      await crossOriginLogoutClear()
    }

    sessionStorage.clear()
    localStorageService.clear()
    jwtTokenService.clearTokens()
    reporter.setUser(null)
    webviewService.clearAppBadges()
    webviewService.logout()

    // redirect 분기:
    //   ① sender + opposite 통합  → opposite /login (Phase 2 정상 흐름)
    //   ② sender + opposite 베이직 → 베이직 학생앱 #/logout (기존 staging 흐름 보존)
    //   ③ receiver                → 자기 /login
    const pair = getDomainPair()
    if (isSenderSide() && pair && isOppositeIntegrated) {
      window.location.replace(`${pair.iframeOrigin}${routeName.login}`)
    } else if (isSenderSide() && !isOppositeIntegrated) {
      envUtils.redirectToBasicStudentApp()
    } else {
      window.location.replace(loginPath)
    }
  }

  requestToken() {
    jwtTokenService.requestToken()
  }

  requestLogout() {
    jwtTokenService.clearTokens()
  }
}

const authService = new AuthService()
export default authService
